import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { RankingItem } from '@/types/rankings';
import { Loader2, Music, Youtube } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import Slider from 'react-slick';
import { toast } from 'sonner';

// Import slick-carousel styles
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface AllTimeSongRankingsProps {
  rankings: RankingItem[];
  loading: boolean;
  error: string | null;
  isDragging: boolean;
  setIsDragging: (isDragging: boolean) => void;
  isMobile: boolean;
  handleSearchResultClick: (title: string, artist: string) => void;
  handleCreatePlaylist: () => void;
  isCreatingPlaylist: boolean;
}

const AllTimeSongRankings: React.FC<AllTimeSongRankingsProps> = ({
  rankings,
  loading,
  error,
  isDragging,
  setIsDragging,
  isMobile,
  handleSearchResultClick,
  handleCreatePlaylist,
  isCreatingPlaylist,
}) => {
  const getRankColor = useCallback((index: number) => {
    switch (index) {
      case 0:
        return 'bg-yellow-500'; // Gold
      case 1:
        return 'bg-gray-400'; // Silver
      case 2:
        return 'bg-amber-700'; // Bronze
      default:
        return 'bg-gray-700';
    }
  }, []);

  const renderRankingItem = useCallback(
    (item: RankingItem, index: number, showRank = true) => {
      const isTopThree = index < 3;
      let itemClasses =
        'flex items-center p-3 rounded-lg mb-2 hover:bg-gray-600 transition-colors cursor-pointer'; // Base classes

      if (isTopThree) {
        itemClasses += ' bg-gray-700'; // Lighter background for top 3
        if (index === 0) itemClasses += ' border-l-4 border-yellow-500';
        else if (index === 1) itemClasses += ' border-l-4 border-gray-400';
        else if (index === 2) itemClasses += ' border-l-4 border-amber-700';
      } else {
        itemClasses += ' bg-gray-800'; // Normal background
      }

      const handleClick = () => {
        if (isDragging) {
          return; // Prevent click if dragging
        }
        handleSearchResultClick(item.title, item.artist);
      };

      const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      };

      return (
        <button
          key={`${item.artist}-${item.title}-${index}`}
          type="button"
          className={`${itemClasses} w-full`} // Add w-full to ensure consistent width
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          {showRank && (
            // Rank badge styling remains the same, using getRankColor
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full mr-4 ${getRankColor(index)} flex-shrink-0`}
            >
              {' '}
              {/* Added flex-shrink-0 */}
              <span className="text-white font-bold">{index + 1}</span>
            </div>
          )}
          {/* Make sure content div adjusts */}
          <div className={`flex-1 overflow-hidden ${!showRank ? 'ml-2' : ''}`}>
            <h3 className="text-sm font-medium text-white truncate">{item.title}</h3>
            <p className="text-xs text-gray-400 truncate">{item.artist}</p>
          </div>
          <div className="flex items-center ml-2">
            <Music className="w-3 h-3 text-blue-400 mr-1" />
            <span className="text-xs text-blue-300 font-medium">{item.count}</span>
          </div>
        </button>
      );
    },
    [isDragging, handleSearchResultClick, getRankColor]
  );

  // --- Responsive Configuration --- //
  const itemsPerSlide = isMobile ? 10 : 5;
  const numSlidesToShow = isMobile ? 1 : 2;
  const numSlidesToScroll = isMobile ? 1 : 2;

  // --- Dynamically generate slides based on current itemsPerSlide --- //
  const slides = useMemo(() => {
    const generatedSlides = [];
    for (let i = 0; i < rankings.length; i += itemsPerSlide) {
      const slideItems = rankings.slice(i, i + itemsPerSlide);
      if (slideItems.length > 0) {
        generatedSlides.push(
          <div key={`song-slide-${i / itemsPerSlide}`}>
            <div className="space-y-2 px-2 w-full">
              {' '}
              {/* Padding for items within a slide */}
              {slideItems.map((item, index) => renderRankingItem(item, i + index, true))}
            </div>
          </div>
        );
      }
    }
    return generatedSlides;
  }, [rankings, itemsPerSlide, renderRankingItem]); // Recalculate if rankings or mode change

  // --- Carousel Settings --- //
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: numSlidesToShow,
    slidesToScroll: numSlidesToScroll,
    arrows: false,
    beforeChange: () => setIsDragging(true),
    afterChange: () => setIsDragging(false),
    appendDots: (dots: React.ReactNode) => (
      <div style={{ position: 'absolute', bottom: '-25px' }}>
        <ul style={{ margin: '0px' }}> {dots} </ul>
      </div>
    ),
    customPaging: (i: number) => (
      <div
        style={{
          width: '10px',
          height: '10px',
          background: 'gray',
          borderRadius: '50%',
          display: 'inline-block',
          margin: '0 5px',
        }}
      />
    ),
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 sm:p-6 shadow-lg mb-8 mx-2 sm:mx-0">
      <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
        <Music className="w-5 h-5 text-blue-400" /> {/* Icon for songs */}
        All-Time Song Rankings (Top 100)
      </h2>
      {error ? (
        <div className="p-4 bg-red-900/50 border border-red-700 rounded-md text-white">
          <p className="mb-2">{error}</p>
          <p className="text-sm text-red-300 mb-3">
            페이지를 새로고침하거나, Supabase에서 함수가 올바르게 업데이트되었는지 확인하세요.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 px-3 py-1 bg-red-700 rounded hover:bg-red-600 transition-colors"
          >
            페이지 새로고침
          </button>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="animate-spin rounded-full h-12 w-12 text-blue-500" />
        </div>
      ) : rankings.length === 0 ? (
        <p className="text-gray-400">No song ranking data available</p>
      ) : (
        <div className="slick-container mb-12">
          {' '}
          {/* Keep increased margin for dots */}
          {/* Add key to Slider to force re-render on layout change */}
          <div className="w-full">
            <Slider key={isMobile ? 'mobile' : 'desktop'} {...settings}>
              {slides}
            </Slider>
          </div>
        </div>
      )}

      {/* Create Playlist button */}
      {!loading && !error && rankings.length > 0 && (
        <div className="mt-8 text-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleCreatePlaylist}
                variant="outline"
                size="sm"
                disabled={isCreatingPlaylist || loading}
                className="relative transition-colors duration-150 ease-in-out hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105"
              >
                {isCreatingPlaylist ? (
                  <Loader2 className="h-5 w-5 md:h-4 md:w-4 md:mr-1.5 animate-spin" />
                ) : (
                  <Youtube className="h-5 w-5 md:h-4 md:w-4 md:mr-1.5 text-[#FF0000]" />
                )}
                <span>Create Playlist (Top 100 Songs)</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Top 100 곡 순위로 YouTube 플레이리스트 생성</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default AllTimeSongRankings;
